import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

interface TeamOption {
  label: string;
  value: string; // full entity ref, e.g. group:default/platform_engineering
}

export const AsyncTeamSelect = ({
  onChange,
  value,
  required,
}: FieldExtensionComponentProps<string>) => {
  const catalogApi = useApi(catalogApiRef);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>(value || '');
  const [error, setError] = useState<string | null>(null);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await catalogApi.getEntities({
        filter: { kind: 'Group', 'spec.type': 'dev-team' },
        fields: ['kind', 'metadata.name', 'metadata.namespace', 'metadata.title'],
        limit: 500,
      });

      const sorted = response.items
        .map(entity => ({
          label: entity.metadata.title || entity.metadata.name,
          value: stringifyEntityRef(entity),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setTeams(sorted);
      setError(null);
    } catch (err) {
      setError('Failed to fetch teams.');
    }
  }, [catalogApi]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value as string;
    setSelectedTeam(newValue);
    onChange?.(newValue);
  };

  return (
    <FormControl margin="normal" required={required} error={!!error} fullWidth>
      <InputLabel id="team-select-label">Team</InputLabel>
      <Select labelId="team-select-label" value={selectedTeam} onChange={handleChange}>
        {teams.map(team => (
          <MenuItem key={team.value} value={team.value}>
            {team.label}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!error && <FormHelperText>Select the team that will own this resource.</FormHelperText>}
    </FormControl>
  );
};
